// @ts-nocheck
import React, { useState, useEffect, useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useLocation, useNavigate, useOutletContext, Link } from 'react-router-dom';
import { useAuth, useUser } from '@/auth/clerkShim';
import { useAdminAuth } from '@/auth/AuthProvider';
import _ from 'lodash';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import useRegistrationStore from '@store/registrationStore';
import useOrganizationStore from '@store/organizationStore';
import useLeagueStore from '@store/leagueStore';
import useTournamentStore from '@store/tournamentStore';
import usePaymentStore from '@store/paymentStore';
import useSummaryStore from '@store/summaryStore';
import { StripeRequirementsBannerContainer } from '@/components/StripeEmbeddedOnboarding';
import { PageContainer, PageHeader } from '@/components/Page/PageContainer';
import { StatCardsSkeleton, ListSkeleton } from '@/components/Page/PageSkeletons';
import { EmptyState } from '@/components/Page/EmptyState';
import { AnimatedNumber } from '@/components/Page/AnimatedNumber';
import useStripeConnectStatus from '@/hooks/useStripeConnectStatus';
import helpers from '@/utils/helpers';
import formatDateOnly from '@/utils/formatDateOnly';
import { TourLauncher } from '@/components/TourLauncher';
import useTourStore from '@/store/tourStore';
import useAdminStore from '@/store/adminStore';
import { TOURS, getTourById } from '@/tours';

// Icons
import { 
  TrendingUp, 
  Users, 
  Trophy, 
  DollarSign, 
  Calendar, 
  Settings, 
  Plus,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  Target,
  CheckCircle,
  Clock,
  Star,
  Zap,
  PlayCircle,
  Sparkles
} from 'lucide-react';

// Bootstrap auto-creates a league/tournament + divisions + Home layout for
// every new org, so hasLayout/hasTournaments/hasSeasons were true on day one
// and the checklist showed 3/7 done with zero user effort. Every item below is
// a deliberate action.
// Month-over-month delta under a stat value. Renders BOTH directions (the old
// inline version silently hid negative changes); success/destructive are the
// semantic token colors.
// Tiny trend line for a stat card: no axes, indigo stroke + soft gradient
// fill. `id` must be unique per instance (SVG gradient ids are global).
function Sparkline({ data, dataKey, id }) {
  // No line at all until there's an actual signal — an all-zero series draws
  // a flat stroke that reads as a stray underline.
  if (!data || data.length < 2 || !data.some((d) => Number(d?.[dataKey]) > 0)) return null;
  return (
    <div className="-mb-2 mt-3 h-8" aria-hidden="true">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.25} />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey={dataKey}
            stroke="hsl(var(--primary))"
            strokeWidth={1.5}
            fill={`url(#${id})`}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// A monthly series is "empty" when it has no points OR every point is zero.
// The backend always returns a full 12-month window (zero-filled), so a plain
// `.length > 0` check never falls through to the EmptyState and the chart
// renders a flat zero-line that reads as broken (prod e2e 2026-10-13). Show
// the chart only when at least one value is non-zero.
export function hasNonZero(series: any[] | undefined, key: string): boolean {
  return Array.isArray(series) && series.some((pt) => Number(pt?.[key]) > 0);
}

// The one Analytics chart treatment: indigo gradient area, quiet axes (no
// axis lines/ticks), token grid, elevated tooltip. Replaces three ad-hoc
// LineCharts that each used a different raw hex (green/blue/amber).
function ThemedAreaChart({ data, dataKey, xKey, id, valueFormatter, label }) {
  return (
    <ResponsiveContainer width="100%" height={280}>
      <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.25} />
            <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
        <XAxis
          dataKey={xKey}
          tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
          tickLine={false}
          axisLine={false}
          width={56}
          tickFormatter={valueFormatter}
        />
        <Tooltip
          formatter={(value) => [valueFormatter(Number(value)), label]}
          labelFormatter={(l) => `${l}`}
          contentStyle={{
            borderRadius: 8,
            border: '1px solid hsl(var(--border))',
            boxShadow: '0 4px 8px -2px hsl(220 23% 15% / 0.06), 0 12px 24px -4px hsl(220 23% 15% / 0.09)',
            fontSize: 12,
          }}
        />
        <Area
          type="monotone"
          dataKey={dataKey}
          stroke="hsl(var(--primary))"
          strokeWidth={2}
          fill={`url(#${id})`}
          dot={false}
          activeDot={{ r: 4 }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function StatDelta({ value }) {
  if (!value) return null;
  const up = value > 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <div className={`mt-1 flex items-center text-xs font-medium ${up ? 'text-success' : 'text-destructive'}`}>
      <Icon className="mr-1 h-3 w-3" />
      {up ? '+' : ''}{value}% from last month
    </div>
  );
}

const steps = [
  // INT-0049: the organizer's job first (teams → schedule), money last.
  // "Add Teams" counts REAL teams (the bootstrap seeds example teams into
  // every org, so a raw DivisionTeam count would always read done).
  { name: "Add Teams", link: "/admin/organize", key: "realTeamsCount" },
  { name: "Publish the Schedule", link: "/admin/scheduler", key: "gamesCount" },
  { name: "Customize Your Competition", link: "/admin/organize", key: "builtFirstStructure" },
  // ADR 0038: bootstrap creates a registration for every new org, so
  // hasRegistration is constant-true — the deliberate action is REVIEWING it.
  { name: "Review Your Registration", link: "/admin/get-started", key: "reviewedRegistration" },
  // Pricing page retired 2026-08-25 — fees are created inline on each
  // registration's Payments pane (key kept: summary still reports it).
  { name: "Set Registration Pricing", link: "/admin/registration", key: "hasPaymentProducts" },
  { name: "Set Up Payments", link: null, key: "createdStripeAccount" },
];

const businessEntityOptions = [
  { value: "individual", label: "Individual" },
  { value: "llc", label: "LLC" },
  { value: "corporation", label: "Corporation" },
  { value: "partnership", label: "Partnership" },
  { value: "nonprofit", label: "Non-Profit" },
];

function DashboardAdmin() {
  const location = useLocation();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { admin } = useAdminAuth();
  const { user } = useUser();
  const [selectedBusinessEntity, setSelectedBusinessEntity] = useState("");
  const [businessEmail, setBusinessEmail] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const { leagueName, league } = useOutletContext() as any;
  // Bind getToken outside the selector — calling the factory inside returns a
  // fresh function per run, an ever-changing snapshot that forces re-renders
  // (double-created ConnectInstances in the Stripe banner; see
  // StripeEmbeddedOnboarding).
  const stripeSessionFactory = useOrganizationStore(
    (state: any) => state.createStripeAccountSession
  );
  const createStripeAccountSession = useMemo(
    () => stripeSessionFactory(getToken), [stripeSessionFactory, getToken]
  );

  console.log('DashboardAdmin::league::', league, leagueName);
  console.log('DashboardAdmin::admin::', admin);

  // Access state and actions from Zustand store
  const { registrations, getAllRegistrations } = useRegistrationStore((state: any) => ({
    registrations: state.registrations,
    getAllRegistrations: state.getAllRegistrations,
  }));

  const { payments, getPaymentsByLeagueName } = usePaymentStore((state: any) => ({
    payments: state.payments,
    getPaymentsByLeagueName: state.getPaymentsByLeagueName,
  }));

  const { seasons } = useLeagueStore((state: any) => ({
    seasons: state.leagues,
  }));

  const { tournaments } = useTournamentStore((state: any) => ({
    tournaments: state.tournaments,
  }));

  const { summary, recent, upcoming, monthlyRevenue, teamsRegistered, cloudflareAnalytics, setup, getSummary, getRecent, getUpcoming, getMonthlyRevenue, getTeamsRegistered, getCloudflareAnalytics, getSetup } = useSummaryStore((state: any) => ({
    summary: state.summary,
    recent: state.recent,
    upcoming: state.upcoming,
    monthlyRevenue: state.monthlyRevenue,
    teamsRegistered: state.teamsRegistered,
    cloudflareAnalytics: state.cloudflareAnalytics,
    setup: state.setup,
    getSummary: state.getSummary(getToken),
    getRecent: state.getRecent(getToken),
    getUpcoming: state.getUpcoming(getToken),
    getMonthlyRevenue: state.getMonthlyRevenue(getToken),
    getTeamsRegistered: state.getTeamsRegistered(getToken),
    getCloudflareAnalytics: state.getCloudflareAnalytics(getToken),
    getSetup: state.getSetup(getToken),
  }));

  // Tour state
  const { completedTours, toursDismissed, syncFromServer, markCompleted, hasCompleted } = useTourStore();
  const adminFromStore = useAdminStore((state: any) => state.admin);
  const clerkId = adminFromStore?.provider?.id;

  // Sync tours from admin data
  useEffect(() => {
    if (adminFromStore?._id) {
      syncFromServer(adminFromStore);
    }
  }, [adminFromStore?._id]);

  const handleStartTour = (tourId: string) => {
    const tour = getTourById(tourId as any);
    if (tour && clerkId) {
      tour.startTour(() => {
        markCompleted(tourId as any, clerkId, getToken);
      }, navigate);
    }
  };

  const showGettingStartedCard = !toursDismissed && completedTours.length < TOURS.length;

  // Skeleton only until the FIRST fetch round resolves — with cached store data
  // (module-singleton Zustand stores persist across navigations) content paints
  // immediately and refreshes silently.
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.all([
      getSummary(league._id),
      getRecent(league._id),
      getUpcoming(league._id),
      getMonthlyRevenue(league._id),
      getTeamsRegistered(league._id),
      getCloudflareAnalytics(league._id),
      getSetup(league._id),
    ]).finally(() => setLoaded(true));
  }, []);

  console.log('DashboardAdmin::summary::', summary);

  const [showRegistration, setShowRegistration] = useState(false);

  const showRegistrationForm = () => {
    setShowRegistration(true);
  };

  const async_getPaymentsByLeagueName = async (leagueName) => {
    const token = await getToken();
    await getPaymentsByLeagueName(leagueName, token);
  };

  useEffect(() => {
    async_getPaymentsByLeagueName(leagueName);
  }, []);

  console.log('DashboardAdmin::payments::', payments);

  // Stripe "action needed" banner (ADR 0044): only mounted when the live
  // status reports outstanding requirements — healthy orgs never mint a
  // session. Embedded onboarding itself lives in StripeSetupModal, opened
  // from Get Started / the registration Payments pane.
  const dashOrgId = helpers.getObjId(admin?.organizations?.[0]);
  const { status: dashStripeStatus } = useStripeConnectStatus(dashOrgId);
  const stripeNeedsAttention =
    !!dashStripeStatus?.hasAccount && (dashStripeStatus?.requirementsDue?.length || 0) > 0;

  const goCreateRegistration = () => {
    navigate(`/create-tournament/1`);
  };

  useEffect(() => {
    getAllRegistrations(leagueName);
    console.log('getSeasonsByLeagueName');
  }, []);

  useEffect(() => {
    if (user) {
      setBusinessEmail(user.primaryEmailAddress.emailAddress);
    }
  }, [user]);

  console.log('DashboardAdmin::registrations::', registrations);

  // Determine setup status from API data. `getSetup` stores the flat result
  // object in `state.setup` (summaryStore), so read `setup` directly; keep the
  // `.result` fallback in case a caller ever stores the wrapped response.
  const setupStatus = setup?.result || setup || {};
  // "Setup Stripe Payments" is done only when charges are actually ENABLED
  // (live status — ADR 0045 tightened the old stamped-at-create flag); if the
  // status endpoint is unavailable, fall back to the stored flag.
  const isStripeSetup = dashStripeStatus && dashStripeStatus.error !== 'unavailable'
    ? !!dashStripeStatus.chargesEnabled
    : !!setupStatus.createdStripeAccount;

  // Calculate completed steps based on setup data
  const completedSteps = React.useMemo(() => {
    let count = 0;
    if (setupStatus.builtFirstStructure || setupStatus.customizedCompetition) count++;
    if (setupStatus.reviewedRegistration) count++;
    if (setupStatus.hasPaymentProducts) count++;
    if (isStripeSetup) count++;
    if ((setupStatus.realTeamsCount || 0) > 0) count++;
    if ((setupStatus.gamesCount || 0) > 0) count++;
    return count;
  }, [setupStatus, isStripeSetup]);

  const progressPercentage = (completedSteps / steps.length) * 100;

  // Mock data for charts and stats
  const mockStats = {
    totalRevenue: 15420,
    revenueChange: 12.5,
    totalTeams: 48,
    teamsChange: 8.2,
    activeTournaments: 6,
    tournamentsChange: 15.3,
    completionRate: 87,
    completionChange: 5.1
  };

  // Combine recent signups and paid teams, sort by date, and take the latest 4
  const recentActivity = React.useMemo(() => {
    const signups = (recent?.recentSignups || []).map((signup, index) => ({
      id: `signup-${index}`,
      type: 'registration',
      message: `${signup.teamName} registered for ${signup.season || signup.tournament || 'tournament'}`,
      time: new Date(signup.signupDate).toLocaleDateString(),
      icon: Users,
      date: new Date(signup.signupDate)
    }));

    const payments = (recent?.recentPaidTeams || []).map((payment, index) => ({
      id: `payment-${index}`,
      type: 'payment',
      message: `Payment received from ${payment.teamName} - $${(payment.amount / 100).toLocaleString()}`,
      time: new Date(payment.paidDate).toLocaleDateString(),
      icon: DollarSign,
      date: new Date(payment.paidDate)
    }));

    // Combine and sort by date (newest first), then take the latest 4
    return [...signups, ...payments]
      .sort((a, b) => b.date - a.date)
      .slice(0, 4)
      .map(item => ({
        ...item,
        date: undefined // Remove date from final object
      }));
  }, [recent]);

  // Combine active tournaments and seasons, sort by date, and take the latest 3
  const upcomingEvents = React.useMemo(() => {
    const tournaments = (upcoming?.activeTournaments || []).map((tournament, index) => ({
      id: `tournament-${index}`,
      name: tournament.name,
      date: tournament.startDate,
      teams: tournament.teamCount,
      status: 'active',
      type: 'tournament',
      registration: tournament.registration
    }));

    const seasons = (upcoming?.activeSeasons || []).map((season, index) => ({
      id: `season-${index}`,
      name: season.name,
      date: season.startDate,
      teams: season.teamCount,
      status: 'active',
      type: 'season',
      tournament: season.tournament
    }));

    // Combine and sort by start date (newest first), then take the latest 3
    return [...tournaments, ...seasons]
      .sort((a, b) => {
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();
        return dateB - dateA;
      })
      .slice(0, 3);
  }, [upcoming]);

  return (
    <PageContainer>
        {stripeNeedsAttention && dashOrgId && (
          <div data-testid="stripe-requirements-banner">
            <StripeRequirementsBannerContainer
              orgId={dashOrgId}
              createStripeAccountSession={createStripeAccountSession}
            />
          </div>
        )}
        {/* Header */}
        <PageHeader
          title={<>Welcome back, {admin?.firstName || 'League Owner'}! 👋</>}
          description={<>Here's what's happening with your {leagueName} league today</>}
          actions={(
            <>
              <TourLauncher variant="icon" />
              <Button variant="outline" size="lg" onClick={() => navigate('/admin/settings')}>
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </Button>
              <Button size="lg" onClick={() => navigate('/admin/organize')}>
                <Plus className="h-4 w-4 mr-2" />
                Create Tournament
              </Button>
            </>
          )}
        />

        {/* Getting Started Tours Card */}
        {showGettingStartedCard && (
          <Card className="overflow-hidden border-0 bg-gradient-to-br from-primary to-primary-deep text-primary-foreground shadow-md">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="h-5 w-5" />
                    <h3 className="text-lg font-semibold">Getting Started with Staty</h3>
                  </div>
                  <p className="text-primary-foreground/80 text-sm mb-4">
                    Take a quick tour to learn how to manage your league. {completedTours.length} of {TOURS.length} completed.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {TOURS.map((tour) => {
                      const isCompleted = hasCompleted(tour.id);
                      return (
                        <button
                          key={tour.id}
                          onClick={() => handleStartTour(tour.id)}
                          className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all ${
                            isCompleted
                              ? 'bg-white/20 text-white/60 cursor-default'
                              : 'bg-white/10 hover:bg-white/20 text-white'
                          }`}
                          disabled={isCompleted}
                        >
                          {isCompleted ? (
                            <CheckCircle className="h-4 w-4" />
                          ) : (
                            <PlayCircle className="h-4 w-4" />
                          )}
                          {tour.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="ml-6 flex-shrink-0">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/20">
                    <span className="text-2xl font-bold">{completedTours.length}/{TOURS.length}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 bg-muted">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="setup" className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              Setup
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Stats Cards — skeleton during the first fetch when nothing is cached */}
            {!loaded && _.isEmpty(summary) ? (
              <StatCardsSkeleton />
            ) : (
            <div className="stagger-children grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="shadow-xs">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Total Revenue</CardTitle>
                  <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10">
                    <DollarSign className="h-4 w-4 text-primary" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-semibold tracking-tight tabular-nums text-foreground">
                    <AnimatedNumber
                      value={(summary.totalRevenue || 0) / 100}
                      format={(n) => `$${Math.round(n).toLocaleString()}`}
                    />
                  </div>
                  <StatDelta value={_.get(summary, 'monthlyChanges.revenue.percentageChange', 0)} />
                  <Sparkline id="spark-revenue" data={monthlyRevenue?.monthlyRevenue} dataKey="revenue" />
                </CardContent>
              </Card>

              <Card className="shadow-xs">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Active Teams</CardTitle>
                  <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10">
                    <Users className="h-4 w-4 text-primary" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-semibold tracking-tight tabular-nums text-foreground">
                    <AnimatedNumber value={_.get(summary, 'totalActiveTeams', 0)} />
                  </div>
                  <StatDelta value={_.get(summary, 'monthlyChanges.activeTeams.percentageChange', 0)} />
                  <Sparkline id="spark-teams" data={teamsRegistered?.monthlyTeamsRegistered} dataKey="teamsRegistered" />
                </CardContent>
              </Card>

              <Card className="shadow-xs">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Active Tournaments</CardTitle>
                  <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10">
                    <Trophy className="h-4 w-4 text-primary" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-semibold tracking-tight tabular-nums text-foreground">
                    <AnimatedNumber value={_.get(summary, 'totalActiveTournaments', 0)} />
                  </div>
                  <StatDelta value={_.get(summary, 'monthlyChanges.activeTournaments.percentageChange', 0)} />
                </CardContent>
              </Card>

              <Card className="shadow-xs">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Completion Rate</CardTitle>
                  <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10">
                    <Target className="h-4 w-4 text-primary" />
                  </div>
                </CardHeader>
                <CardContent>
                  {/* The API already returns a PERCENTAGE — summary.controller
                      computes `paidTeams / totalTeams * 100`. Multiplying again
                      rendered a fully-paid org as 10000%. */}
                  <div className="text-3xl font-semibold tracking-tight tabular-nums text-foreground">
                    <AnimatedNumber
                      value={_.get(summary, 'paidCompletionRate', 0)}
                      format={(n) => `${Math.round(n)}%`}
                    />
                  </div>
                  <StatDelta value={_.get(summary, 'monthlyChanges.paidCompletionRate.percentageChange', 0)} />
                </CardContent>
              </Card>
            </div>
            )}

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Recent Activity */}
              <Card className="lg:col-span-2 shadow-xs">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5" />
                    Recent Activity
                  </CardTitle>
                  <CardDescription>Latest updates from your league</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!loaded && _.isEmpty(recent) && <ListSkeleton rows={4} />}
                  {loaded && recentActivity.length === 0 && (
                    <EmptyState
                      variant="inline"
                      icon={Activity}
                      title="No activity yet"
                      description="Team signups and payments will show up here as they happen."
                    />
                  )}
                  {recentActivity.map((activity) => (
                    <div key={activity.id} className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer">
                      <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                        <activity.icon className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground">{activity.message}</p>
                        <p className="text-xs text-muted-foreground">{activity.time}</p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card className="shadow-xs">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="h-5 w-5" />
                    Quick Actions
                  </CardTitle>
                  <CardDescription>Get things done faster</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button className="w-full justify-start" variant="outline" onClick={() => navigate('/admin/organize')}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Tournament
                  </Button>
                  <Button className="w-full justify-start" variant="outline" onClick={() => navigate('/admin/members')}>
                    <Users className="h-4 w-4 mr-2" />
                    Manage Members
                  </Button>
                  <Button className="w-full justify-start" variant="outline" onClick={() => navigate('/admin/organize')}>
                    <Trophy className="h-4 w-4 mr-2" />
                    View Tournaments
                  </Button>
                  <Button className="w-full justify-start" variant="outline" onClick={() => navigate('/admin/registration')}>
                    <DollarSign className="h-4 w-4 mr-2" />
                    Payment Settings
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Upcoming Events */}
            <Card className="shadow-xs">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Upcoming Events
                </CardTitle>
                <CardDescription>Your next tournaments and important dates</CardDescription>
              </CardHeader>
              <CardContent>
                {!loaded && _.isEmpty(upcoming) && <ListSkeleton rows={3} />}
                {loaded && upcomingEvents.length === 0 && (
                  <EmptyState
                    variant="inline"
                    icon={Calendar}
                    title="Nothing scheduled yet"
                    description="Active tournaments and seasons with start dates will appear here."
                    action={(
                      <Button size="sm" variant="outline" onClick={() => navigate('/admin/organize')}>
                        <Plus className="h-4 w-4 mr-2" />
                        Create a competition
                      </Button>
                    )}
                  />
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {upcomingEvents.map((event) => (
                    <div key={event.id} className="p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-foreground">{event.name}</h4>
                        <Badge variant={event.status === 'active' ? 'default' : 'secondary'}>
                          {event.status}
                        </Badge>
                      </div>
                      {/* startDate is DATE-ONLY (UTC midnight) — local formatting showed the previous day. */}
                      <p className="text-sm text-muted-foreground mb-2">{formatDateOnly(event.date)}</p>
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Users className="h-4 w-4 mr-1" />
                        <span className="tabular-nums">{event.teams}</span>&nbsp;teams
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="shadow-sm border-0">
                <CardHeader>
                  <CardTitle>Revenue Overview</CardTitle>
                  <CardDescription>Monthly revenue trends</CardDescription>
                </CardHeader>
                <CardContent>
                  {hasNonZero(monthlyRevenue?.monthlyRevenue, 'revenue') ? (
                    <ThemedAreaChart
                      data={monthlyRevenue.monthlyRevenue}
                      dataKey="revenue"
                      xKey="monthName"
                      id="chart-revenue"
                      valueFormatter={(value) => `$${(Number(value) / 100).toLocaleString()}`}
                      label="Revenue"
                    />
                  ) : (
                    <EmptyState
                      variant="inline"
                      icon={DollarSign}
                      title="No revenue yet"
                      description="Monthly revenue appears here once payments come in."
                    />
                  )}
                </CardContent>
              </Card>

              <Card className="shadow-sm border-0">
                <CardHeader>
                  <CardTitle>Team Registration</CardTitle>
                  <CardDescription>Team growth over time</CardDescription>
                </CardHeader>
                <CardContent>
                  {hasNonZero(teamsRegistered?.monthlyTeamsRegistered, 'teamsRegistered') ? (
                    <ThemedAreaChart
                      data={teamsRegistered.monthlyTeamsRegistered}
                      dataKey="teamsRegistered"
                      xKey="monthName"
                      id="chart-teams"
                      valueFormatter={(value) => `${value}`}
                      label="Teams"
                    />
                  ) : (
                    <EmptyState
                      variant="inline"
                      icon={Users}
                      title="No registrations yet"
                      description="Team growth appears here as teams sign up."
                    />
                  )}
                </CardContent>
              </Card>

              <Card className="shadow-sm border-0">
                <CardHeader>
                  <CardTitle>Daily Page Views</CardTitle>
                  <CardDescription>Last 7 days of page views from Cloudflare</CardDescription>
                </CardHeader>
                <CardContent>
                  {hasNonZero(cloudflareAnalytics?.dailyUniqueVisitors, 'uniqueVisitors') ? (
                    <ThemedAreaChart
                      data={cloudflareAnalytics.dailyUniqueVisitors}
                      dataKey="uniqueVisitors"
                      xKey="dayName"
                      id="chart-pageviews"
                      valueFormatter={(value) => `${value}`}
                      label="Page views"
                    />
                  ) : (
                    <EmptyState
                      variant="inline"
                      icon={TrendingUp}
                      title="No traffic data yet"
                      description="Page views on your public site appear here."
                    />
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Setup Tab */}
          <TabsContent value="setup" className="space-y-6">
            <Card className="shadow-sm border-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  League Setup Progress
                </CardTitle>
                <CardDescription>Complete these steps to get your league fully operational</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-foreground">Overall Progress</h3>
                      <p className="text-sm text-muted-foreground">{completedSteps} of {steps.length} steps completed</p>
                    </div>
                    <Badge variant="outline" className="text-lg px-3 py-1">
                      {Math.round(progressPercentage)}%
                    </Badge>
                  </div>
                  <Progress value={progressPercentage} className="h-3" />
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-foreground">Setup Steps</h3>
                  {steps.map((step, index) => {
                    let isCompleted = false;
                    let detailText = '';
                    
                    // Check completion status based on step key and setup data
                    switch (step.key) {
                      case "builtFirstStructure":
                        isCompleted = setupStatus.builtFirstStructure || setupStatus.customizedCompetition;
                        break;
                      case "createdStripeAccount":
                        isCompleted = isStripeSetup;
                        break;
                      case "reviewedRegistration":
                        isCompleted = setupStatus.reviewedRegistration;
                        break;
                      case "hasPaymentProducts":
                        isCompleted = setupStatus.hasPaymentProducts;
                        if (isCompleted) {
                          detailText = `${setupStatus.totalPaymentProducts} products created`;
                        }
                        break;
                      case "realTeamsCount":
                        isCompleted = (setupStatus.realTeamsCount || 0) > 0;
                        if (isCompleted) {
                          detailText = `${setupStatus.realTeamsCount} teams added`;
                        }
                        break;
                      case "gamesCount":
                        isCompleted = (setupStatus.gamesCount || 0) > 0;
                        if (isCompleted) {
                          detailText = `${setupStatus.gamesCount} games scheduled`;
                        }
                        break;
                      default:
                        isCompleted = false;
                    }

                    return (
                      <div key={index} className="flex items-center justify-between p-4 border border-border rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            isCompleted ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground/60'
                          }`}>
                            {isCompleted ? <CheckCircle className="h-5 w-5" /> : <Clock className="h-5 w-5" />}
                          </div>
                          <div>
                            <p className="font-medium text-foreground">{step.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {isCompleted ? 'Completed' : 'Pending'}
                            </p>
                            {isCompleted && detailText && (
                              <p className="text-xs text-success">
                                {detailText}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Single canonical onboarding surface — the guided Get Started page. */}
                <div className="rounded-lg border border-info/30 bg-info/10 p-4 flex items-center justify-between gap-4">
                  <div>
                    <p className="font-semibold text-foreground">Finish setup the guided way</p>
                    <p className="text-sm text-muted-foreground">
                      Add your teams, publish the schedule, and share your site, all in one place.
                    </p>
                  </div>
                  <Button onClick={() => navigate('/admin/get-started')} className="shrink-0">
                    Continue in Get Started
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            <Card className="shadow-sm border-0">
              <CardHeader>
                <CardTitle>League Business Settings</CardTitle>
                <CardDescription>Configure your league's business information and payment settings</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="settings-business-entity">Business Entity</Label>
                    <Select value={selectedBusinessEntity} onValueChange={setSelectedBusinessEntity}>
                      <SelectTrigger id="settings-business-entity">
                        <SelectValue placeholder="Select business entity type" />
                      </SelectTrigger>
                      <SelectContent>
                        {businessEntityOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="settings-business-email">Business Email</Label>
                    <Input
                      id="settings-business-email"
                      type="email"
                      placeholder="Enter your business email"
                      value={businessEmail}
                      onChange={(e) => setBusinessEmail(e.target.value)}
                    />
                  </div>
                </div>
                <Button onClick={() => console.log("Save settings")} className="w-full md:w-auto">
                  Save Settings
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Empty State — never flash this while the initial fetch is in flight */}
        {loaded && registrations.length === 0 && (
          <Card className="shadow-sm border-0">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Trophy className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">Ready to get started?</h3>
              <p className="text-muted-foreground text-center mb-6 max-w-md">
                Complete the setup steps above to start creating tournaments and managing your league.
              </p>
              <Button onClick={() => navigate('/admin/get-started')}>
                Get Started
              </Button>
            </CardContent>
          </Card>
        )}
    </PageContainer>
  );
}

export default DashboardAdmin;
